import axios from 'axios'

import { readCookies } from 'logic/auth'

import { refreshTokenMiddleware, checkForExpiredToken, confirmSessionStatus } from './middlewares'

const BASE_URL = (import.meta.env.VITE_API_URL || 'http://localhost:3000') + '/v1'

refreshTokenMiddleware(axios)
checkForExpiredToken(axios)
confirmSessionStatus(axios)

const request = async function (req: Record<string, unknown>) {
	try {
		const response = await axios(req as any)
		return response
	} catch (e: unknown) {
		if (axios.isAxiosError<{ error_message?: string; [key: string]: unknown }>(e) && e.response) {
			const {
				status,
				data: { error_message, ...rest },
			} = e.response
			const err = { status, errorMessage: error_message || 'Failed to perform request, please try again.', ...rest }
			throw err
		} else if (axios.isAxiosError(e)) {
			const errObject = e.toJSON() as { code?: string; message?: string }
			const err = { status: errObject.code, errorMessage: errObject.message, isNetworkError: true }
			throw err
		} else {
			const message = e instanceof Error ? e.message : 'An unexpected error occurred'
			throw { status: 'UNKNOWN', errorMessage: message, isNetworkError: true }
		}
	}
}

function getDefaultHeaders() {
	return {
		'Content-type': 'application/json',
		'X-SMSLEOPARD-TOKEN': window.localStorage.getItem('x-smsleopard-token') || readCookies().auth_token || '',
		'X-SMSLEOPARD-ACCOUNT-ID': window.localStorage.getItem('x-smsleopard-account-id') || readCookies().account_id || '',
		'Accept-Language': window.localStorage.getItem('i18nextLng') || readCookies().preferred_language || '',
	}
}

type RequestParameters = {
	params?: {}
	url: string
	headers?: {}
	data?: {}
}

class Request {
	static get({ params = {}, url, headers }: RequestParameters): Promise<any> {
		return request({
			method: 'GET',
			url: BASE_URL + url,
			params,
			headers: headers || getDefaultHeaders(),
		})
	}

	static post({ data = {}, params = {}, headers = {}, url }: RequestParameters): Promise<any> {
		return request({
			method: 'POST',
			url: BASE_URL + url,
			data,
			params,
			headers: { ...getDefaultHeaders(), ...headers },
		})
	}

	static put({ data = {}, params = {}, url, headers }: RequestParameters): Promise<any> {
		return request({
			method: 'PUT',
			url: BASE_URL + url,
			data,
			params,
			headers: headers || getDefaultHeaders(),
		})
	}

	static patch({ data = {}, params = {}, url, headers }: RequestParameters): Promise<any> {
		return request({
			method: 'PATCH',
			url: BASE_URL + url,
			data,
			params,
			headers: headers || getDefaultHeaders(),
		})
	}

	static delete({ params = {}, data = {}, url, headers }: RequestParameters): Promise<any> {
		return request({
			method: 'DELETE',
			url: BASE_URL + url,
			data,
			params,
			headers: headers || getDefaultHeaders(),
		})
	}

	static filedownload({ url, params = {}, headers }: RequestParameters): Promise<any> {
		return request({
			url: BASE_URL + url,
			method: 'GET',
			responseType: 'blob',
			params,
			headers: headers || getDefaultHeaders(),
		})
	}

	static externalFileDownload({ url }: { url: string }): Promise<any> {
		return request({
			url: url,
			method: 'GET',
			responseType: 'blob',
		})
	}
}

export default Request
